/**
 * Native Invoice Context — Firestore is source of truth. No Xero.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import { collection, getDocs, getFirestore, query, orderBy, limit } from 'firebase/firestore';
import { authFetch } from '../../../utils/cloudFunctionsAuth';
import { Invoice, InvoiceFilters, InvoiceStatus, LineItem } from '../../../types/accounting';
import { isDateOverdue } from '../../../utils/dateUtils';

const CF_BASE = 'https://us-central1-web-construct-app.cloudfunctions.net';

export interface OutstandingSummary {
  invoiceCount: number;
  totalOutstanding: number;
  overdueAmount: number;
  overdueCount: number;
}

interface InvoiceContextState {
  invoices: Invoice[];
  invoicesLoading: boolean;
  invoicesError: string | null;
  invoiceFilters: InvoiceFilters;
  outstandingSummary: OutstandingSummary | null;
  summaryLoading: boolean;
  createInvoiceLoading: boolean;
  updateInvoiceLoading: boolean;
  invoiceDetailLoading: boolean;
  toggleSentLoading: string | null;
  invoiceActionLoading: string | null;
  connectionStatus: { connected: boolean };
}

interface InvoiceContextActions {
  setInvoiceFilters: (filters: Partial<InvoiceFilters>) => void;
  fetchInvoices: (filters?: Partial<InvoiceFilters>) => Promise<void>;
  createInvoice: (payload: {
    contactId?: string;
    contactName: string;
    contactEmail?: string;
    reference?: string;
    date?: string;
    dueDate?: string;
    currencyCode?: string;
    status?: 'DRAFT' | 'APPROVED';
    lineAmountTypes?: 'Exclusive' | 'Inclusive' | 'NoTax';
    lineItems: Array<{ description: string; quantity: number; unitAmount: number; taxType?: 'GST' | 'GST_FREE' | 'NONE'; accountCode?: string }>;
  }) => Promise<{ invoice: Invoice }>;
  updateInvoice: (payload: {
    invoiceId: string;
    contactId?: string;
    contactName?: string;
    contactEmail?: string;
    reference?: string;
    date?: string;
    dueDate?: string;
    status?: 'DRAFT' | 'APPROVED';
    lineAmountTypes?: 'Exclusive' | 'Inclusive' | 'NoTax';
    lineItems?: Array<{ description: string; quantity: number; unitAmount: number; taxType?: 'GST' | 'GST_FREE' | 'NONE'; accountCode?: string }>;
  }) => Promise<{ invoice: Invoice }>;
  fetchInvoiceDetail: (invoiceId: string) => Promise<Invoice | null>;
  voidInvoice: (invoiceId: string) => Promise<void>;
  deleteDraftInvoice: (invoiceId: string) => Promise<void>;
  approveInvoice: (invoiceId: string) => Promise<void>;
  toggleInvoiceSent: (invoiceId: string, sent: boolean) => Promise<void>;
  emailInvoice: (invoiceId: string) => Promise<{ draftId: string; to: string }>;
  getOnlineInvoiceUrl: (invoiceId: string) => Promise<string | null>;
  fetchPayments: (invoiceId: string) => Promise<any[]>;
  recordPayment: (payload: { invoiceId: string; method: string; amount: number; date?: string; reference?: string }) => Promise<void>;
  deletePayment: (paymentId: string, invoiceId: string) => Promise<void>;
  bankAccounts: any[];
  paymentsLoading: string | null;
  recordPaymentLoading: boolean;
  deletePaymentLoading: string | null;
}

type InvoiceContextType = InvoiceContextState & InvoiceContextActions;

const defaultFilters: InvoiceFilters = {
  status: 'APPROVED',
};

const InvoiceContext = createContext<InvoiceContextType | null>(null);

export function useInvoice(): InvoiceContextType {
  const ctx = useContext(InvoiceContext);
  if (!ctx) throw new Error('useInvoice must be used within InvoiceProvider');
  return ctx;
}

export function InvoiceProvider({ children }: { children: ReactNode }) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [invoicesError, setInvoicesError] = useState<string | null>(null);
  const [invoiceFilters, setInvoiceFiltersState] = useState<InvoiceFilters>(defaultFilters);
  const [outstandingSummary, setOutstandingSummary] = useState<OutstandingSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [createInvoiceLoading, setCreateInvoiceLoading] = useState(false);
  const [updateInvoiceLoading, setUpdateInvoiceLoading] = useState(false);
  const [invoiceDetailLoading, setInvoiceDetailLoading] = useState(false);
  const [toggleSentLoading, setToggleSentLoading] = useState<string | null>(null);
  const [invoiceActionLoading, setInvoiceActionLoading] = useState<string | null>(null);
  const [paymentsLoading, setPaymentsLoading] = useState<string | null>(null);
  const [recordPaymentLoading, setRecordPaymentLoading] = useState(false);
  const [deletePaymentLoading, setDeletePaymentLoading] = useState<string | null>(null);

  const setInvoiceFilters = useCallback((filters: Partial<InvoiceFilters>) => {
    setInvoiceFiltersState(prev => ({ ...prev, ...filters }));
  }, []);

  const fetchInvoices = useCallback(async (overrides?: Partial<InvoiceFilters>) => {
    const filters = { ...invoiceFilters, ...overrides };
    setInvoicesLoading(true);
    setSummaryLoading(true);
    setInvoicesError(null);
    try {
      const db = getFirestore();
      const q = query(
        collection(db, 'invoices'),
        orderBy('dueDate', 'desc'),
        limit(2000)
      );
      const snap = await getDocs(q);
      const allInvoices: Invoice[] = snap.docs.map(d => ({ id: d.id, ...d.data() } as Invoice));

      // KPI summary is computed over ALL invoices, INDEPENDENT of the table
      // filters — otherwise "Total Outstanding" reads $0 whenever the user
      // filters to e.g. Paid/Draft (audit: high). amountDue is already
      // credit-aware on the server.
      const outstanding = allInvoices.filter(inv => inv.status === 'APPROVED' || inv.status === 'SENT');
      const totalOutstanding = outstanding.reduce((sum, inv) => sum + (inv.amountDue || 0), 0);
      const overdue = outstanding.filter(inv => isDateOverdue(inv.dueDate));
      const overdueAmount = overdue.reduce((sum, inv) => sum + (inv.amountDue || 0), 0);
      setOutstandingSummary({
        invoiceCount: outstanding.length,
        totalOutstanding,
        overdueCount: overdue.length,
        overdueAmount,
      });

      // The table list IS filter-dependent.
      let list = allInvoices;
      if (filters.status) {
        const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
        list = list.filter(inv => {
          const effectiveStatus = inv.status === 'SENT' ? 'APPROVED' : inv.status;
          return statuses.includes(effectiveStatus);
        });
      }
      if (filters.contactId) list = list.filter(inv => inv.contactId === filters.contactId);
      if (filters.dateFrom) list = list.filter(inv => inv.dueDate >= filters.dateFrom!);
      if (filters.dateTo) list = list.filter(inv => inv.dueDate <= filters.dateTo!);
      if (filters.search) {
        const s = filters.search.toLowerCase();
        list = list.filter(inv => {
          if ((inv.invoiceNumber || '').toLowerCase().includes(s)) return true;
          if ((inv.contactName || '').toLowerCase().includes(s)) return true;
          if ((inv.reference || '').toLowerCase().includes(s)) return true;
          // Line-item description search (added 2026-05). Most useful
          // when users remember what they billed for but not the
          // invoice number — "fence repair", "site visit", etc.
          const lines = Array.isArray(inv.lineItems) ? inv.lineItems : [];
          for (const li of lines) {
            if ((li?.description || '').toLowerCase().includes(s)) return true;
          }
          return false;
        });
      }

      setInvoices(list);
    } catch (e: any) {
      setInvoicesError(e?.message || 'Failed to fetch invoices');
      setInvoices([]);
    } finally {
      setInvoicesLoading(false);
      setSummaryLoading(false);
    }
  }, [invoiceFilters]);

  useEffect(() => {
    fetchInvoices();
  }, [invoiceFilters.status, invoiceFilters.contactId, invoiceFilters.dateFrom, invoiceFilters.dateTo, invoiceFilters.search]);

  const createInvoice = useCallback(async (payload: any) => {
    setCreateInvoiceLoading(true);
    try {
      const res = await authFetch(`${CF_BASE}/createInvoiceHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Create failed');
      await fetchInvoices();
      return { invoice: data.invoice as Invoice };
    } finally {
      setCreateInvoiceLoading(false);
    }
  }, [fetchInvoices]);

  const updateInvoice = useCallback(async (payload: any) => {
    setUpdateInvoiceLoading(true);
    try {
      const res = await authFetch(`${CF_BASE}/updateInvoiceHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Update failed');
      await fetchInvoices();
      return { invoice: data.invoice as Invoice };
    } finally {
      setUpdateInvoiceLoading(false);
    }
  }, [fetchInvoices]);

  const fetchInvoiceDetail = useCallback(async (invoiceId: string): Promise<Invoice | null> => {
    setInvoiceDetailLoading(true);
    try {
      const res = await authFetch(`${CF_BASE}/getInvoiceHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoiceId }),
      });
      const data = await res.json();
      if (!data.ok) return null;
      return data.invoice as Invoice;
    } finally {
      setInvoiceDetailLoading(false);
    }
  }, []);

  const voidInvoice = useCallback(async (invoiceId: string) => {
    setInvoiceActionLoading(invoiceId);
    try {
      const res = await authFetch(`${CF_BASE}/voidInvoiceHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoiceId }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error);
      await fetchInvoices();
    } finally {
      setInvoiceActionLoading(null);
    }
  }, [fetchInvoices]);

  const deleteDraftInvoice = useCallback(async (invoiceId: string) => {
    setInvoiceActionLoading(invoiceId);
    try {
      const res = await authFetch(`${CF_BASE}/deleteInvoiceHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoiceId }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error);
      await fetchInvoices();
    } finally {
      setInvoiceActionLoading(null);
    }
  }, [fetchInvoices]);

  const approveInvoice = useCallback(async (invoiceId: string) => {
    setInvoiceActionLoading(invoiceId);
    try {
      const res = await authFetch(`${CF_BASE}/approveInvoiceHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoiceId }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error);
      await fetchInvoices();
    } finally {
      setInvoiceActionLoading(null);
    }
  }, [fetchInvoices]);

  const toggleInvoiceSent = useCallback(async (invoiceId: string, sent: boolean) => {
    setToggleSentLoading(invoiceId);
    try {
      const res = await authFetch(`${CF_BASE}/markInvoiceSentHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoiceId, sent }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error);
      await fetchInvoices();
    } finally {
      setToggleSentLoading(null);
    }
  }, [fetchInvoices]);

  // "Email invoice" now has the agent DRAFT an AI-written email (grounded in
  // customer context, invoice PDF attached) into the user's draft queue for
  // review + manual send — it never sends immediately. Returns the draft info.
  const emailInvoice = useCallback(async (invoiceId: string): Promise<{ draftId: string; to: string }> => {
    const res = await authFetch(`${CF_BASE}/draftInvoiceEmailHttp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invoiceId }),
    });
    const data = await res.json();
    if (!data.ok) throw new Error(data.error || 'Could not draft the email');
    return { draftId: String(data.draftId || ''), to: String(data.to || '') };
  }, []);

  const getOnlineInvoiceUrl = useCallback(async (_invoiceId: string): Promise<string | null> => {
    return null;
  }, []);

  const fetchPayments = useCallback(async (invoiceId: string): Promise<any[]> => {
    setPaymentsLoading(invoiceId);
    try {
      const res = await authFetch(`${CF_BASE}/getPaymentsHttp?documentType=invoice&documentId=${encodeURIComponent(invoiceId)}`, { method: 'GET' });
      const data = await res.json();
      if (!data.ok) return [];
      return data.payments || [];
    } catch {
      return [];
    } finally {
      setPaymentsLoading(prev => (prev === invoiceId ? null : prev));
    }
  }, []);

  const recordPayment = useCallback(async (payload: { invoiceId: string; method: string; amount: number; date?: string; reference?: string }) => {
    setRecordPaymentLoading(true);
    try {
      const res = await authFetch(`${CF_BASE}/recordPaymentHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentType: 'invoice',
          documentId: payload.invoiceId,
          amount: payload.amount,
          date: payload.date,
          method: payload.method || 'OTHER',
          reference: payload.reference,
        }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Record payment failed');
      await fetchInvoices();
    } finally {
      setRecordPaymentLoading(false);
    }
  }, [fetchInvoices]);

  const deletePayment = useCallback(async (paymentId: string, _invoiceId: string) => {
    setDeletePaymentLoading(paymentId);
    try {
      const res = await authFetch(`${CF_BASE}/deletePaymentHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentId }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Delete payment failed');
      await fetchInvoices();
    } finally {
      setDeletePaymentLoading(null);
    }
  }, [fetchInvoices]);

  return (
    <InvoiceContext.Provider
      value={{
        invoices,
        invoicesLoading,
        invoicesError,
        invoiceFilters,
        outstandingSummary,
        summaryLoading,
        createInvoiceLoading,
        updateInvoiceLoading,
        invoiceDetailLoading,
        toggleSentLoading,
        invoiceActionLoading,
        connectionStatus: { connected: true },
        setInvoiceFilters,
        fetchInvoices,
        createInvoice,
        updateInvoice,
        fetchInvoiceDetail,
        voidInvoice,
        deleteDraftInvoice,
        approveInvoice,
        toggleInvoiceSent,
        emailInvoice,
        getOnlineInvoiceUrl,
        fetchPayments,
        recordPayment,
        deletePayment,
        bankAccounts: [],
        paymentsLoading,
        recordPaymentLoading,
        deletePaymentLoading,
      }}
    >
      {children}
    </InvoiceContext.Provider>
  );
}
